import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LowPriorityIcon from "@mui/icons-material/LowPriority";

import { useWitnessReorder } from "./useWitnessReorder";
import WitnessReorderList from "./WitnessReorderList";

const WitnessReorderModal = ({ open, onClose, nota, token, refresh }) => {
  const { witnesses, hasChanges, loading, handleDragEnd, handleSave } =
    useWitnessReorder({ open, nota, token, onClose, refresh });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)"
              : "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <ModalHeader onClose={onClose} />

      <Divider />

      <NoteTitle titulo={nota?.titulo} />

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
        <WitnessReorderList witnesses={witnesses} onDragEnd={handleDragEnd} />
      </DialogContent>

      <Divider />

      <ModalActions
        onClose={onClose}
        onSave={handleSave}
        loading={loading}
        disabled={!hasChanges || witnesses.length === 0}
      />
    </Dialog>
  );
};

const ModalHeader = ({ onClose }) => (
  <DialogTitle
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      pb: 1,
      pt: 2.5,
      px: 3,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 2,
        bgcolor: "success.main",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <LowPriorityIcon sx={{ color: "#fff", fontSize: 20 }} />
    </Box>

    <Box sx={{ flex: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        Reordenar Testigos
      </Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Arrastra el ícono ≡ para cambiar el orden
      </Typography>
    </Box>

    <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
);

const NoteTitle = ({ titulo }) => (
  <Box sx={{ px: 3, pt: 2, pb: 1 }}>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 700,
        fontSize: "13px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: "text.primary",
      }}
    >
      {titulo}
    </Typography>
  </Box>
);

const ModalActions = ({ onClose, onSave, loading, disabled }) => (
  <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
    <Button
      variant="outlined"
      color="inherit"
      onClick={onClose}
      disabled={loading}
      sx={{ textTransform: "none", borderRadius: 2 }}
    >
      Cancelar
    </Button>
    <Button
      variant="contained"
      color="success"
      onClick={onSave}
      disabled={disabled || loading}
      startIcon={
        loading ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          <LowPriorityIcon />
        )
      }
      sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
    >
      {loading ? "Guardando..." : "Guardar orden"}
    </Button>
  </DialogActions>
);

export default WitnessReorderModal;
